import { useState, useEffect, useCallback } from 'react';
import { MessageSquare, ChevronDown, ChevronRight, Pencil, Trash2, Loader2 } from 'lucide-react';
import { authHeader } from '../../utils/authHeaders';
import { BASE_QUERYURL } from '../constants';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useTranslation } from '../../i18n/LanguageContext';
import DeleteModal from './DeleteModal';

const api = async (path, options = {}) => {
  const res = await fetch(`${BASE_QUERYURL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
};

// Every answer mounts one of these at once; they share a single fetch.
const inflight = new Map();

const loadHistoryComments = (historyId) => {
  if (!inflight.has(historyId)) {
    const p = api(`/answer-comments/by-history?history_id=${encodeURIComponent(historyId)}`)
      .finally(() => setTimeout(() => inflight.delete(historyId), 0));
    inflight.set(historyId, p);
  }
  return inflight.get(historyId);
};

const AnswerComments = ({ questionId, historyId, open: openProp, onCountChange }) => {
  const { t } = useTranslation();
  const { aispaceColor } = useThemeColor();
  const controlled = openProp !== undefined;
  const [openSelf, setOpenSelf] = useState(false);
  const open = controlled ? openProp : openSelf;
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [pending, setPending] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const busy = pending !== null;

  useEffect(() => {
    if (!questionId || loaded) return;
    let cancelled = false;
    setLoading(true);
    const load = historyId
      ? loadHistoryComments(historyId).then((d) => (d?.comments_by_question || {})[questionId] || [])
      : api(`/answer-comments?question_id=${encodeURIComponent(questionId)}`).then((d) => d?.comments || []);
    load
      .then((list) => {
        if (cancelled) return;
        setComments(list);
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setError(t('share.commentsLoadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [questionId, historyId, loaded, t]);

  useEffect(() => {
    if (onCountChange) onCountChange(comments.length);
  }, [comments.length, onCountChange]);

  const submit = useCallback(async (key, fn) => {
    setPending(key);
    setError(null);
    try {
      const data = await fn();
      setComments(data?.comments || []);
      setLoaded(true);
      return true;
    } catch {
      setError(t('share.commentsSaveError'));
      return false;
    } finally {
      setPending(null);
    }
  }, [t]);

  const add = async () => {
    const text = draft.trim();
    if (!text) return;
    const ok = await submit('add', () => api('/answer-comments', {
      method: 'POST',
      body: JSON.stringify({ question_id: questionId, comment_text: text }),
    }));
    if (ok) setDraft('');
  };

  const saveEdit = async (id) => {
    const text = editDraft.trim();
    if (!text) return;
    const ok = await submit(`save:${id}`, () => api(`/answer-comments/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ comment_text: text }),
    }));
    if (ok) setEditingId(null);
  };

  const confirmRemove = async () => {
    const id = deletingId;
    if (!id) return;
    const ok = await submit(`delete:${id}`, () =>
      api(`/answer-comments/${encodeURIComponent(id)}`, { method: 'DELETE' }));
    if (ok) setDeletingId(null);
  };

  const formatWhen = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
  };

  if (!questionId) return null;
  if (controlled && !open && !deletingId) return null;

  return (
    <div className={controlled ? 'mt-2' : 'mt-3 border-t border-[#EEEEEE] pt-2'}>
      {!controlled && (
        <button
          type="button"
          onClick={() => setOpenSelf((v) => !v)}
          className="flex items-center gap-1.5 text-[13px] text-[#6B7280] hover:text-[#111827]"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <MessageSquare size={14} />
          <span>
            {t('share.commentsTitle')}
            {loaded && comments.length > 0 ? ` (${comments.length})` : ''}
          </span>
        </button>
      )}

      {open && (
        <div className="mt-2">
          {error && <p className="text-[12px] text-red-600 mb-2">{error}</p>}

          {loading && !loaded && (
            <div className="flex items-center gap-2 text-[12px] text-[#9CA3AF] mb-2">
              <Loader2 size={13} className="animate-spin" />
              {t('share.commentsLoading')}
            </div>
          )}

          {loaded && comments.length === 0 && !error && (
            <p className="text-[12px] text-[#9CA3AF] mb-2">{t('share.commentsEmpty')}</p>
          )}

          <ul className="space-y-2 mb-3">
            {comments.map((c) => (
              <li key={c.id} className="bg-[#FAFAFA] border border-[#EEEEEE] rounded-lg px-3 py-2">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[12px] font-medium text-[#374151] truncate">
                    {c.author_name || c.author_email || t('share.commentsUnknownAuthor')}
                  </span>
                  <span className="text-[11px] text-[#9CA3AF] whitespace-nowrap">
                    {formatWhen(c.created_at)}
                    {c.updated_at ? ` · ${t('share.commentsEdited')}` : ''}
                  </span>
                </div>

                {editingId === c.id ? (
                  <div>
                    <textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={2}
                      maxLength={4000}
                      className="w-full text-[13px] border border-[#E5E7EB] rounded-md px-2 py-1.5 focus:outline-none focus:border-[#9CA3AF] resize-y"
                    />
                    <div className="flex gap-2 mt-1.5">
                      <button
                        type="button"
                        onClick={() => saveEdit(c.id)}
                        disabled={busy || !editDraft.trim()}
                        className="flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-md text-white disabled:opacity-40"
                        style={{ backgroundColor: aispaceColor }}
                      >
                        {pending === `save:${c.id}` && <Loader2 size={12} className="animate-spin" />}
                        {t('share.commentsSave')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        disabled={busy}
                        className="text-[12px] px-2.5 py-1 rounded-md border border-[#E5E7EB] text-[#374151] disabled:opacity-40"
                      >
                        {t('share.commentsCancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-[13px] text-[#374151] whitespace-pre-wrap break-words">{c.comment_text}</p>
                    {c.is_mine && (
                      <div className="flex gap-3 mt-1.5">
                        <button
                          type="button"
                          onClick={() => { setEditingId(c.id); setEditDraft(c.comment_text); }}
                          disabled={busy}
                          className="flex items-center gap-1 text-[12px] text-[#6B7280] hover:text-[#111827] disabled:opacity-40"
                        >
                          <Pencil size={12} /> {t('share.commentsEdit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeletingId(c.id)}
                          disabled={busy}
                          className="flex items-center gap-1 text-[12px] text-[#6B7280] hover:text-red-600 disabled:opacity-40"
                        >
                          <Trash2 size={12} /> {t('share.commentsDelete')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>

          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('share.commentsPlaceholder')}
            rows={2}
            maxLength={4000}
            disabled={busy}
            className="w-full text-[13px] border border-[#E5E7EB] rounded-md px-2 py-1.5 focus:outline-none focus:border-[#9CA3AF] resize-y disabled:opacity-60"
          />
          <button
            type="button"
            onClick={add}
            disabled={busy || !draft.trim()}
            className="mt-1.5 flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md text-white disabled:opacity-40"
            style={{ backgroundColor: aispaceColor }}
          >
            {pending === 'add' && <Loader2 size={12} className="animate-spin" />}
            {t('share.commentsAdd')}
          </button>
        </div>
      )}

      <DeleteModal
        isOpen={!!deletingId}
        onClose={() => setDeletingId(null)}
        onDelete={confirmRemove}
        message={t('share.commentsDeleteConfirm')}
      />
    </div>
  );
};

export default AnswerComments;
